import React from 'react'
import {
  marketingCheckoutCartUpdate,
  marketingCheckoutConfirmCreate,
  marketingCheckoutContactCreate,
  marketingCheckoutPaymentCreate,
  marketingCheckoutRetrieve,
  marketingCheckoutShiftCreate,
  marketingCheckoutStartCreate,
  type CartRequest,
  type CartWithMarketRequest,
  type CheckoutContactRequest,
  type CheckoutShiftRequest,
} from '@sidegigllc/api/client'
import { useSelectedMarket } from '../../providers/Market'
import { getStoredAttribution } from '../../utils/appUrl'

export type Session = { id: string; token: string }

const withMarket = (
  cart: CartRequest,
  marketId: number | undefined,
): CartWithMarketRequest => {
  if (marketId === undefined) throw new Error('No market selected for checkout')
  return { ...cart, market_id: marketId }
}

const buildHeaders = (token: string) => ({
  headers: { 'X-Gigpro-Checkout-Token': token },
})

export const useCheckoutSession = (initialSession: Session | null = null) => {
  const [session, setSession] = React.useState<Session | null>(initialSession)
  const startingRef = React.useRef<Promise<Session> | null>(null)
  const marketId = useSelectedMarket().market?.marketId

  const ensureSession = React.useCallback(
    async (cart: CartRequest): Promise<Session> => {
      if (session) return session
      if (startingRef.current) return startingRef.current
      const p = (async () => {
        const res = await marketingCheckoutStartCreate({
          ...withMarket(cart, marketId),
          attribution: getStoredAttribution(),
        })
        const next: Session = { id: res.session_id, token: res.session_token }
        setSession(next)
        return next
      })()
      startingRef.current = p
      try {
        return await p
      } finally {
        startingRef.current = null
      }
    },
    [session, marketId],
  )

  const submitContact = React.useCallback(
    (s: Session, data: CheckoutContactRequest) =>
      marketingCheckoutContactCreate(s.id, data, buildHeaders(s.token)),
    [],
  )

  const withSession = React.useCallback(
    <T>(fn: (s: Session) => Promise<T>): Promise<T> => {
      if (!session) throw new Error('Checkout session not initialized')
      return fn(session)
    },
    [session],
  )

  const submitShift = React.useCallback(
    (data: CheckoutShiftRequest) =>
      withSession((s) =>
        marketingCheckoutShiftCreate(s.id, data, buildHeaders(s.token)),
      ),
    [withSession],
  )

  const submitCart = React.useCallback(
    (cart: CartRequest) =>
      withSession((s) =>
        marketingCheckoutCartUpdate(
          s.id,
          withMarket(cart, marketId),
          buildHeaders(s.token),
        ),
      ),
    [withSession, marketId],
  )

  const submitPayment = React.useCallback(
    () =>
      withSession((s) =>
        marketingCheckoutPaymentCreate(s.id, buildHeaders(s.token)),
      ),
    [withSession],
  )

  const submitConfirm = React.useCallback(
    () =>
      withSession((s) =>
        marketingCheckoutConfirmCreate(s.id, buildHeaders(s.token)),
      ),
    [withSession],
  )

  const retrieveSession = React.useCallback(
    () =>
      withSession((s) =>
        marketingCheckoutRetrieve(s.id, buildHeaders(s.token)),
      ),
    [withSession],
  )

  const reset = React.useCallback(() => {
    setSession(null)
    startingRef.current = null
  }, [])

  return {
    session,
    ensureSession,
    submitContact,
    submitCart,
    submitShift,
    submitPayment,
    submitConfirm,
    retrieveSession,
    reset,
  }
}
